import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Building2, Package, Users, User, Menu } from "lucide-react";
import ApiService from "../../services/api";
import DetailsTab from "./tabs/DetailsTab";
import ProductsTab from "./tabs/ProductsTab";
import UsersTab from "./tabs/UsersTab";
import PatientsTab from "./tabs/PatientsTab";
import UserProfileDropdown from "../../components/common/UserProfileDropdown";
import AdminLayout from "./AdminLayout";

interface Clinic {
  clinic_id: number;
  external_clinic_id: string;
  clinic_name: string;
  legal_name: string;
  registration_no: string;
  tax_id: string;
  street_address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  email: string;
  phone_number: string;
  timezone: string;
  preferred_language: string;
  notes: string;
  is_active: boolean;
  created_at: string;
  white_label_settings: {
    logo: string;
    email: string;
    logo_url: string;
    subdomain: string;
    about_link: string;
    brand_name: string;
    app_password: string;
    contact_link: string;
    primary_color: string;
    terms_of_service_link: string;
    privacy_statement_link: string;
  };
}

type TabType = "details" | "products" | "users" | "patients";

const AdminAccountDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isAddUser = searchParams.get("is_add_user") === "true";
  const isViewDetails = searchParams.get("is_view_details") === "true";
  const isViewProducts = searchParams.get("is_view_products") === "true";
  const activeTabParam = searchParams.get("active_tab") as TabType;
  const { accountId: clinicId } = useParams<{ accountId: string }>();
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("details");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClinicDetails();
  }, []);

  useEffect(() => {
    if (isAddUser) {
      setActiveTab("users");
    }
  }, [isAddUser]);

  useEffect(() => {
    if (isViewDetails) {
      setActiveTab("details");
    }
  }, [isViewDetails]);

  useEffect(() => {
    if (isViewProducts) {
      setActiveTab("products");
    }
  }, [isViewProducts]);

  useEffect(() => {
    if (activeTabParam) {
      setActiveTab(activeTabParam);
    }
  }, [activeTabParam]);

  const fetchClinicDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await ApiService.searchClinics({ clinic_id: clinicId });

      if (result.success) {
        setClinic(result.data[0]);
      } else {
        setError("Account not found");
      }
    } catch (err: any) {
      console.error("Error fetching account details:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch account details"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {error || "Account not found"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              The account you're looking for doesn't exist or there was an error
              loading it.
            </p>
            <button
              onClick={fetchClinicDetails}
              className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "details" as TabType, name: "Details", icon: Building2 },
    { id: "products" as TabType, name: "Products", icon: Package },
    { id: "users" as TabType, name: "Users", icon: Users },
    { id: "patients" as TabType, name: "Patients", icon: User },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "details":
        return <DetailsTab clinic={clinic} />;
      case "products":
        return <ProductsTab clinicId={clinic.clinic_id} />;
      case "users":
        return <UsersTab clinicId={clinic.clinic_id} isAddUser={isAddUser} />;
      case "patients":
        return (
          <PatientsTab
            clinicId={clinic.clinic_id}
            accountId={clinic.external_clinic_id}
          />
        );
      default:
        return <DetailsTab clinic={clinic} />;
    }
  };

  return (
    <AdminLayout title="Account Details">
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            {/* <button className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                  <ArrowLeft className="h-5 w-5" />
                </button> */}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {clinic.clinic_name}
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                {clinic.legal_name} • Created {formatDate(clinic.created_at)}
              </p>
            </div>
          </div>
          <div className="mt-3 sm:mt-0 flex items-center space-x-2 justify-start sm:justify-end">
            <span
              className={`inline-flex items-center px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm font-medium ${
                clinic.is_active
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {clinic.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-4 w-4 mr-1 sm:mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </AdminLayout>
  );
};

export default AdminAccountDetails;
